import React, { useState } from "react";
import { Check, Copy, Loader2, Puzzle, Shield, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useFigmaPluginToken } from "@/hooks/useFigmaPlugin";

// The plugin is presented as the recommended route rather than an advanced alternative,
// because for anyone without a paid Figma Dev seat it is the *only* route that works: REST
// reads are metered per user and a restricted seat allows roughly six a month.

const ADVANTAGES = [
  { icon: Zap, text: "No rate limits and no paid Figma seat needed" },
  { icon: Shield, text: "Reads Figma's own Dev Mode CSS for the closest possible match" },
  { icon: Check, text: "Your real design tokens, on any Figma plan" },
];

interface FigmaPluginPanelProps {
  /**
   * False for Figma View and Collab seats, whose REST allowance is about six reads a month.
   *
   * This changes the recommendation rather than the availability. A user who already pays for a
   * Dev seat should be told to paste a link — pushing an install step at someone whose account
   * already works is friction for nothing. A restricted seat needs the plugin, because pasting a
   * link genuinely will not work for them.
   */
  seatSupportsImport?: boolean;
  /**
   * Whether the Figma account is actually connected.
   *
   * Separate from the seat on purpose: the seat decides whether pasting a link *can* work, the
   * connection decides whether it works *yet*. Claiming "you're already set up" next to a card
   * reading "Not connected" is the kind of contradiction that makes a user distrust the screen.
   */
  connected?: boolean;
}

const FigmaPluginPanel: React.FC<FigmaPluginPanelProps> = ({
  seatSupportsImport = true,
  connected = false,
}) => {
  const { info, issued, plaintext, loading, error, issue, revoke, dismissPlaintext } =
    useFigmaPluginToken();
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);

  const copy = async () => {
    if (!plaintext) return;
    try {
      await navigator.clipboard.writeText(plaintext);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard can be blocked; the token stays visible so it can be selected manually
    }
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden dark:border-white/10 dark:bg-white/5">
      <div className="flex items-start gap-2 border-b border-gray-100 bg-gray-50/70 px-4 py-3 dark:border-white/10 dark:bg-white/5">
        <Puzzle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        <div className="min-w-0">
          <p className="text-xs font-semibold text-foreground">
            Joylo plugin for Figma
            {seatSupportsImport ? (
              <span className="ml-2 inline-flex items-center rounded-full bg-gray-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-muted-foreground dark:bg-white/10 dark:text-gray-400">
                Optional
              </span>
            ) : (
              <span className="ml-2 inline-flex items-center rounded-full bg-emerald-50 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-400">
                Recommended for your seat
              </span>
            )}
          </p>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            {!seatSupportsImport
              ? "Runs inside Figma, so it works on any plan — including yours."
              : connected
                ? "Your seat already supports pasting a Figma link. Use the plugin only when you want the closest possible match."
                : "Connect Figma above to paste links, or use the plugin instead — it needs no connection."}
          </p>
        </div>
      </div>

      <div className="space-y-3 p-4">
        {seatSupportsImport && connected && (
          <p className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs leading-relaxed text-foreground dark:border-white/10 dark:bg-white/5 dark:text-gray-300">
            <strong className="text-foreground">You're already set up.</strong> Paste a Figma link
            into the prompt box and Joylo will build it — no install needed.
          </p>
        )}

        <ul className="space-y-1.5">
          {ADVANTAGES.map(({ icon: Icon, text }) => (
            <li key={text} className="flex gap-2 text-xs leading-relaxed text-foreground">
              <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-success" />
              <span>{text}</span>
            </li>
          ))}
        </ul>

        <ol className="space-y-1 border-t border-gray-100 pt-3 text-xs text-muted-foreground dark:border-white/10 dark:text-gray-400">
          <li>
            <strong className="text-foreground">1.</strong> Install the Joylo plugin in Figma.
          </li>
          <li>
            <strong className="text-foreground">2.</strong> Create a plugin token below and paste
            it into the plugin once.
          </li>
          <li>
            <strong className="text-foreground">3.</strong> Pick your screens in Figma and press
            Build in Joylo.
          </li>
        </ol>

        {loading && (
          <p className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking your token…
          </p>
        )}

        {error && <p className="text-xs text-destructive">{error}</p>}

        {plaintext && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 dark:border-emerald-900/40 dark:bg-emerald-950/30">
            <p className="mb-2 text-[11px] font-medium text-emerald-900 dark:text-emerald-300">
              Copy this now — it is not shown again.
            </p>
            <div className="flex items-center gap-2">
              <code className="min-w-0 flex-1 truncate rounded border border-emerald-200 bg-white px-2 py-1.5 font-mono text-[11px] text-foreground dark:border-emerald-800/40 dark:bg-white/5 dark:text-white">
                {plaintext}
              </code>
              <Button size="sm" variant="outline" onClick={copy} className="shrink-0 gap-1.5">
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? "Copied" : "Copy"}
              </Button>
            </div>
            <button
              type="button"
              onClick={dismissPlaintext}
              className="mt-2 text-[11px] text-emerald-800 underline dark:text-emerald-400"
            >
              I've saved it
            </button>
          </div>
        )}

        {!loading && !plaintext && (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                await issue();
                setBusy(false);
              }}
              className="gap-1.5"
            >
              {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {issued ? "Create a new token" : "Create plugin token"}
            </Button>
            {issued && (
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  await revoke();
                  setBusy(false);
                }}
              >
                Revoke
              </Button>
            )}
          </div>
        )}

        {issued && info && !plaintext && (
          <p className="text-[11px] text-muted-foreground">
            Active token <span className="font-mono">{info.tokenPrefix}…</span>
            {info.lastUsedAt
              ? ` · last used ${new Date(info.lastUsedAt).toLocaleDateString()}`
              : " · not used yet"}
          </p>
        )}

        {issued && !plaintext && (
          <p className="text-[11px] text-muted-foreground">
            Creating a new token replaces the old one, so the plugin will need the new value.
          </p>
        )}
      </div>
    </div>
  );
};

export default FigmaPluginPanel;
